import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { url } from '../../utils/confserveur';
import { useUser } from '../../context/UserContext';
import GARConnection from './GARConnection';
import { Shield, Lock, User, KeyRound, Eye, EyeOff } from 'lucide-react';

// Composant de fond animé glassmorphisme
const AnimatedBackground = () => (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Blobs SVG animés avec morphing */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
            <defs>
                {/* Gradient 1 - Blue */}
                <linearGradient id="login-blob1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.6" />
                    <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
                </linearGradient>

                {/* Gradient 2 - Cyan */}
                <linearGradient id="login-blob2" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#a5f3fc" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#67e8f9" stopOpacity="0.25" />
                </linearGradient>

                {/* Gradient 3 - Indigo */}
                <linearGradient id="login-blob3" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#c7d2fe" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#a5b4fc" stopOpacity="0.2" />
                </linearGradient>

                {/* Gradient 4 - Sky */}
                <linearGradient id="login-blob4" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.15" />
                </linearGradient>

                {/* Radial glow */}
                <radialGradient id="login-glow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#dbeafe" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#dbeafe" stopOpacity="0" />
                </radialGradient>
            </defs>

            {/* Blob 1 - Grand, haut gauche */}
            <path fill="url(#login-blob1)">
                <animate
                    attributeName="d"
                    dur="8s"
                    repeatCount="indefinite"
                    values="
                        M5,20 Q15,5 30,15 Q45,25 40,40 Q35,55 20,50 Q5,45 5,20;
                        M10,15 Q25,0 35,12 Q45,24 42,42 Q39,60 22,52 Q5,44 10,15;
                        M8,22 Q18,8 32,18 Q46,28 42,45 Q38,62 23,52 Q8,42 8,22;
                        M5,20 Q15,5 30,15 Q45,25 40,40 Q35,55 20,50 Q5,45 5,20
                    "
                />
            </path>

            {/* Blob 2 - Droite milieu */}
            <path fill="url(#login-blob2)">
                <animate
                    attributeName="d"
                    dur="10s"
                    repeatCount="indefinite"
                    values="
                        M70,10 Q90,15 92,35 Q94,55 80,50 Q66,45 68,25 Q70,5 70,10;
                        M75,8 Q95,12 95,32 Q95,52 82,48 Q69,44 70,24 Q71,4 75,8;
                        M68,12 Q88,18 90,38 Q92,58 78,52 Q64,46 66,26 Q68,6 68,12;
                        M70,10 Q90,15 92,35 Q94,55 80,50 Q66,45 68,25 Q70,5 70,10
                    "
                />
            </path>

            {/* Blob 3 - Centre bas */}
            <path fill="url(#login-blob3)">
                <animate
                    attributeName="d"
                    dur="7s"
                    repeatCount="indefinite"
                    values="
                        M35,60 Q50,55 60,68 Q70,81 55,88 Q40,95 32,82 Q24,69 35,60;
                        M38,58 Q55,52 65,65 Q75,78 58,86 Q41,94 34,80 Q27,66 38,58;
                        M33,62 Q48,58 58,70 Q68,82 52,90 Q36,98 30,84 Q24,70 33,62;
                        M35,60 Q50,55 60,68 Q70,81 55,88 Q40,95 32,82 Q24,69 35,60
                    "
                />
            </path>

            {/* Blob 4 - Gauche bas */}
            <path fill="url(#login-blob4)">
                <animate
                    attributeName="d"
                    dur="9s"
                    repeatCount="indefinite"
                    values="
                        M0,55 Q10,50 20,58 Q30,66 25,80 Q20,94 8,88 Q-4,82 0,55;
                        M0,52 Q12,46 24,55 Q36,64 30,78 Q24,92 10,86 Q-4,80 0,52;
                        M0,58 Q8,54 18,62 Q28,70 22,84 Q16,98 6,90 Q-4,82 0,58;
                        M0,55 Q10,50 20,58 Q30,66 25,80 Q20,94 8,88 Q-4,82 0,55
                    "
                />
            </path>

            {/* Blob 5 - Haut droite */}
            <path fill="url(#login-blob1)">
                <animate
                    attributeName="d"
                    dur="6s"
                    repeatCount="indefinite"
                    values="
                        M85,0 Q100,5 98,20 Q96,35 85,32 Q74,29 78,14 Q82,-1 85,0;
                        M88,2 Q102,8 100,22 Q98,36 86,34 Q74,32 79,16 Q84,0 88,2;
                        M83,0 Q98,4 96,18 Q94,32 82,30 Q70,28 76,12 Q82,-4 83,0;
                        M85,0 Q100,5 98,20 Q96,35 85,32 Q74,29 78,14 Q82,-1 85,0
                    "
                />
            </path>

            {/* Petites particules flottantes */}
            <circle cx="15" cy="40" r="1.5" fill="#60a5fa" opacity="0.6">
                <animate attributeName="cy" dur="4s" values="40;35;40" repeatCount="indefinite" />
                <animate attributeName="opacity" dur="4s" values="0.6;0.9;0.6" repeatCount="indefinite" />
            </circle>
            <circle cx="55" cy="25" r="1" fill="#818cf8" opacity="0.5">
                <animate attributeName="cy" dur="5s" values="25;20;25" repeatCount="indefinite" />
            </circle>
            <circle cx="75" cy="45" r="1.2" fill="#67e8f9" opacity="0.5">
                <animate attributeName="cy" dur="3.5s" values="45;40;45" repeatCount="indefinite" />
            </circle>
            <circle cx="25" cy="75" r="1" fill="#93c5fd" opacity="0.6">
                <animate attributeName="cy" dur="4.5s" values="75;70;75" repeatCount="indefinite" />
            </circle>
            <circle cx="90" cy="30" r="0.8" fill="#a5b4fc" opacity="0.4">
                <animate attributeName="cy" dur="3s" values="30;26;30" repeatCount="indefinite" />
            </circle>

            {/* Glow central */}
            <ellipse cx="50" cy="50" rx="35" ry="25" fill="url(#login-glow)">
                <animate attributeName="rx" dur="6s" values="35;40;35" repeatCount="indefinite" />
                <animate attributeName="ry" dur="6s" values="25;30;25" repeatCount="indefinite" />
            </ellipse>
        </svg>

        {/* Grands cercles flous pour effet de profondeur */}
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-blue-200/40 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-40 right-1/4 w-64 h-64 bg-indigo-200/50 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-10 w-56 h-56 bg-cyan-200/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-10 w-48 h-48 bg-sky-100/60 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 left-1/3 w-60 h-60 bg-blue-100/50 rounded-full blur-3xl animate-float-delayed" />

        {/* Grid pattern subtil */}
        <div
            className="absolute inset-0 opacity-[0.02]"
            style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, #3b82f6 1px, transparent 1px)',
                backgroundSize: '40px 40px'
            }}
        />

        {/* Animations CSS */}
        <style>{`
            @keyframes float {
                0%, 100% { transform: translateY(0) scale(1); }
                50% { transform: translateY(-20px) scale(1.05); }
            }
            @keyframes float-delayed {
                0%, 100% { transform: translateY(0) scale(1); }
                50% { transform: translateY(20px) scale(1.02); }
            }
            .animate-float {
                animation: float 8s ease-in-out infinite;
            }
            .animate-float-delayed {
                animation: float-delayed 10s ease-in-out infinite;
            }
        `}</style>
    </div>
);
const Login = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isGARLogin, setIsGARLogin] = useState(false);
    const [garMessage, setGarMessage] = useState('');
    const [isGARLogout, setIsGARLogout] = useState(false); 

    
    const { user, setUser ,setIsAuthenticated} = useUser();

    useEffect(() => {
        checkGARLogin();
    }, []);

    const checkGARLogin = async () => {
         // 🔧 NE PAS détecter GAR si déjà connecté !
        if (user) {
           
            return;
        }
        // Vérifier si on vient du GAR
        const urlParams = new URLSearchParams(window.location.search);
      
        // Vérifier si c'est une déconnexion GAR
        const reason = urlParams.get('reason');
        if (reason === 'gar_logout') {
            setIsGARLogout(true);
            setGarMessage( 
                        <p className='text-xl text-gray-700'>
                            Vous avez été déconnecté de TanoEd via le GAR.
                        </p>
                     )
            return;
        }
        else if( reason === 'session_expired') {
            setIsGARLogout(true);
            setGarMessage( 
                        <p className='text-xl text-gray-700'>
                            Vous avez été déconnecté de TanoEd car votre session a expiré.
                        </p>
                   )
            return;
        }
        // console.log("urlParams string: ", urlParams.toString())
        // console.log("urlParams entries: ", Object.fromEntries(urlParams))
        // console.log("has ticket?", urlParams.has('ticket'))
        // console.log("has sso?", urlParams.has('sso'))
        // console.log("document.referrer:", document.referrer)
        // const isFromGAR = urlParams.has('ticket') ||
        //                  urlParams.has('sso') ||
        //                  document.referrer.includes('https://simulent.partenaire.test-gar.education.fr/') ||
        //                  document.referrer.toLowerCase().includes('gar') ||
        //                  document.referrer.includes('test-gar.education.fr');

          const isFromGAR = true;

                    if (isFromGAR) {
                    setIsGARLogin(true);
                    setGarMessage('Connexion avec le GAR détectée...');
                    setTimeout(() => {
                        window.location.href = `${url}api/token/?sso=gar`;
                    }, 1000);
                }
    };
// Dans votre composant Login, au montage
useEffect(() => {
    // Nettoyer dès l'arrivée sur la page de login
   localStorage.clear();

}, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(
                `${url}api/token/`,
                { username, password },
                { withCredentials: true }
            );

            setUser(response.data);
           
            localStorage.setItem("iduser", response.data.id);
            navigate(`/home/`);

        } catch (error) {
            setError('Login failed! Veuillez vérifier vos identifiants.');
        } finally {
            setLoading(false);
        }
    };
    // StandardLoginPage non utilisé - supprimé

    // Si connexion GAR en cours, afficher un écran d'attente
    if (isGARLogin) {
        return (
            <div className='relative min-h-screen w-full bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 flex items-center justify-center'>
                <AnimatedBackground />
                <div className="relative z-10">
                    <GARConnection />
                </div>
            </div>
        );
    }

  // Si déconnexion GAR, afficher la page spéciale
    if (isGARLogout) {
        return (
            <div className='relative min-h-screen w-full bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 flex items-center justify-center p-4'>
                <AnimatedBackground />
                <div className="relative z-10 flex flex-col gap-6 items-center p-8 sm:p-10
                    rounded-3xl bg-white/70 backdrop-blur-xl border border-white/50 shadow-2xl max-w-2xl">

                    {/* Icône de déconnexion */}
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                        <svg className="w-8 h-8 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                    </div>

                    <h1 className='font-bold text-2xl sm:text-3xl text-gray-800'>Déconnexion réussie</h1>

                    <div className="text-center space-y-4">
                       {garMessage}
                        <p className='text-base sm:text-lg text-gray-600'>
                            Pour vous reconnecter, veuillez passer par votre <strong>ENT </strong>
                            et cliquer sur l'application TanoEd.
                        </p>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mt-4">
                        <p className="text-sm text-orange-700 flex items-center gap-2">
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            La connexion directe n'est pas possible pour les comptes GAR
                        </p>
                    </div>
                </div>
            </div>
        );
    }
    // Formulaire normal pour connexion standard
   else{ return (
         <GAROnlyPage />
    //    <LoginPage handleSubmit={handleSubmit}
    //    username = {username} setUsername={setUsername} password={password} setPassword={setPassword}
    //    error={error} loading={loading} />
    );
}
};


const GAROnlyPage = () => {
  return (
    <div className='relative min-h-screen w-full bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 flex items-center justify-center p-4'>
      <AnimatedBackground />

      <div className="relative z-10 flex flex-col gap-6 items-center p-8 sm:p-10
        rounded-3xl bg-white/70 backdrop-blur-xl border border-white/50 shadow-2xl max-w-md">

        {/* Icône GAR/Sécurité */}
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 p-4 rounded-2xl shadow-lg shadow-blue-500/30">
          <Shield className="w-full h-full text-white" />
        </div>

        <h1 className='font-bold text-xl sm:text-2xl text-gray-800 text-center'>
          Connexion via GAR uniquement
        </h1>

        <div className="flex flex-col gap-4">
          <p className='text-center text-base sm:text-lg text-gray-600'>
            L'accès à cette application est exclusivement réservé aux utilisateurs authentifiés via le
            <span className="font-semibold text-blue-600"> Gestionnaire d'Accès aux Ressources (GAR)</span>.
          </p>

          <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-4 mt-2">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-800 font-medium mb-1">
                  Connexion sécurisée requise
                </p>
                <p className="text-sm text-gray-600">
                  Veuillez vous connecter depuis votre ENT ou portail éducatif pour accéder à cette ressource.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            Si vous rencontrez des difficultés, contactez votre administrateur ENT
          </p>
        </div>
      </div>
    </div>
  );
};


const LoginPage = ({handleSubmit,username,setUsername,password,setPassword,error,loading}) =>{
    const [showPassword, setShowPassword] = useState(false);
    return (
        <div className='relative min-h-screen w-full bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 flex items-center justify-center p-4'>
            <AnimatedBackground />

            <form className="relative z-10 flex flex-col gap-6 items-center w-full max-w-md p-8 sm:p-10
                rounded-3xl bg-white/70 backdrop-blur-xl border border-white/50 shadow-2xl"
                onSubmit={handleSubmit}>

                {/* Logo/Icône */}
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-2">
                    <Lock className="w-8 h-8 text-white" />
                </div>

                <h1 className='font-bold text-2xl sm:text-3xl text-gray-800'>Identification</h1>
                <p className='text-center text-base sm:text-lg text-gray-600'>
                    Veuillez entrer votre identifiant et votre mot de passe.
                </p>

                <div className="w-full flex flex-col text-base space-y-4">
                    <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            className='w-full pl-12 pr-4 py-4
                                rounded-xl bg-white/80 border border-gray-200
                                focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100
                                text-gray-800 outline-none transition-all
                                placeholder:text-gray-400'
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Identifiant"
                        />
                    </div>

                    <div className="relative">
                        <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            className='w-full pl-12 pr-12 py-4
                                bg-white/80 border border-gray-200
                                focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100
                                rounded-xl text-gray-800 outline-none transition-all
                                placeholder:text-gray-400'
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Mot de passe"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(prev => !prev)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="w-full bg-red-50 border border-red-200 rounded-xl p-3">
                        <p className="text-red-700 text-center text-sm font-medium">{error}</p>
                    </div>
                )}

                <button
                    className='w-full bg-gradient-to-r from-blue-500 to-indigo-600
                        hover:from-blue-600 hover:to-indigo-700
                        hover:-translate-y-0.5
                        uppercase text-lg sm:text-xl
                        text-white font-semibold py-4 rounded-xl
                        transition-all duration-200
                        shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40
                        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0'
                    disabled={loading}
                    type="submit">
                    {loading ? (
                        <span className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Connexion...
                        </span>
                    ) : "Se connecter"}
                </button>
            </form>
        </div>
    )
}
export default Login;